// Import React helpers.
import React, {
    useEffect,
} from "react";



// Export the policy navigation component.
export const PolicyNavigation = ({
    policies = [],
    activeSectionKey,
    onSectionSelect,
    navigationScrollRef,
}) => {

    
    // Scroll the left navigation so the active section remains visible.
    useEffect(() => {

        // Stop when there is no active section.
        if (!activeSectionKey) {

            // Exit the effect.
            return;

        }

        // Get the scrollable navigation container.
        const navigationScrollElement = navigationScrollRef.current;

        // Stop when the navigation container is not available.
        if (!navigationScrollElement) {

            // Exit the effect.
            return;

        }

        // Find the active navigation link inside the scrollable container.
        const activeNavigationLink =
            navigationScrollElement.querySelector(
                `[data-section-key="${activeSectionKey}"]`
            );

        // Stop when the active navigation link cannot be found.
        if (!activeNavigationLink) {

            // Exit the effect.
            return;

        }

        // Get the navigation container boundaries.
        const navigationBounds =
            navigationScrollElement.getBoundingClientRect();

        // Get the active link boundaries.
        const activeLinkBounds =
            activeNavigationLink.getBoundingClientRect();



        // Check whether the active link is above the visible navigation area.
        const activeLinkIsAbove =
            activeLinkBounds.top < navigationBounds.top;

        // Check whether the active link is below the visible navigation area.
        const activeLinkIsBelow =
            activeLinkBounds.bottom > navigationBounds.bottom;



        // Only move the left navigation when the active link is not visible.
        if (activeLinkIsAbove || activeLinkIsBelow) {

            // Scroll the active navigation link into view.
            activeNavigationLink.scrollIntoView({

                // Keep the movement smooth.
                behavior: "smooth",

                // Try to position the active item around the middle.
                block: "center",

                // Avoid changing horizontal scrolling.
                inline: "nearest",

            });

        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSectionKey]);



    // Render the policy navigation.
    return (

        // Render the navigation column.
        <aside className="policy-navigation">

            {/* Render the desktop navigation. */}
            <nav
                className="policy-navigation__desktop"
                aria-label="Privacy policy sections"
            >

                {/* Render the navigation heading. */}
                <h2 className="policy-navigation__title">
                    On this page
                </h2>

                {/* Create the independently scrollable section list. */}
                <div
                    className="policy-navigation__scroll"
                    ref={navigationScrollRef}
                >

                    {/* Render the section links. */}
                    <ul className="policy-navigation__list">

                        {/* Loop through the policy sections. */}
                        {policies.map((policy) => {

                            // Determine whether this policy is currently active.
                            const sectionIsActive =
                                activeSectionKey === policy?.section_key;



                            // Render the navigation item.
                            return (

                                // Render the individual navigation item.
                                <li
                                    className="policy-navigation__item"
                                    key={policy?.section_key}
                                >

                                    {/* Link to the corresponding policy section. */}
                                    <a
                                        className={[
                                            "policy-navigation__link",
                                            sectionIsActive
                                                ? "policy-navigation__link--active"
                                                : "",
                                        ].filter(Boolean).join(" ")}
                                        href={`#${policy?.section_key}`}
                                        data-section-key={policy?.section_key}
                                        aria-current={sectionIsActive ? "true" : undefined}
                                        onClick={(event) => {

                                            // Prevent the browser's default anchor jump.
                                            event.preventDefault();

                                            // Select and scroll to this section.
                                            onSectionSelect(policy?.section_key);

                                        }}
                                    >
                                        {policy?.section}
                                    </a>

                                </li>

                            );

                        })}

                    </ul>

                </div>

            </nav>

            {/* Render the mobile section selector. */}
            <div className="policy-navigation__mobile">

                {/* Label the section selector. */}
                <label
                    className="policy-navigation__mobile-label"
                    htmlFor="policy-section-navigation"
                >
                    Jump to section
                </label>

                {/* Allow mobile users to select a section. */}
                <select
                    className="policy-navigation__select"
                    id="policy-section-navigation"
                    value={activeSectionKey ?? ""}
                    onChange={(event) => {

                        // Select and scroll to the chosen section.
                        onSectionSelect(event.target.value);

                    }}
                >

                    {/* Render each policy section as an option. */}
                    {policies.map((policy) => (

                        // Render the section option.
                        <option
                            key={policy?.section_key}
                            value={policy?.section_key}
                        >
                            {policy?.section}
                        </option>

                    ))}

                </select>

            </div>

        </aside>

    );

};